import React, {
  FunctionComponent,
  MutableRefObject,
  useReducer,
  useState,
} from 'react';

import { useIntl } from 'react-intl';

import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';

import { ContentSection } from '../Section';
import Logo from '../Logo';

import WizardSteps, { WizardSecondaryStep } from './WizardSteps';
import WizardNavigation from './WizardNavigation';
import {
  stepReducer,
  initialStepState,
  formReducer,
  formInitialState,
} from './reducer';
import backendClient from '../../../../services/Backend/backendClient';
import {
  SpotifyUser,
  SpotifyArtist,
} from '../../../../services/Spotify/spotifyClient.types';
import { useBackendLoginHook } from '../../../../services/Backend/useBackendLoginHook';
import {
  UserMetaArtists,
  UserMetaGenres,
} from '../../../../services/Backend/useBackendLoginHook.types';
import CircleAnimations from '../Animations/CircleAnimation';

type User = {
  spotifyId: string;
  artists: UserMetaArtists | null;
  genres: UserMetaGenres | null;
};

type UserModel = {
  spotifyId: string;
  artists: string[];
  genres: string[];
};

type ClubModal = {
  users: UserModel[];
  externalArtists: string[];
  externalArtistsExtended: string[];
  externalGenres: string[];
};

type WizardWrapper = {
  noLogin: boolean;
  onLogout: () => void;
  sectionRef: MutableRefObject<any>;
  user: SpotifyUser | null;
  artists: {
    longTerm: SpotifyArtist[] | null;
    mediumTerm: SpotifyArtist[] | null;
    shortTerm: SpotifyArtist[] | null;
  };
};

const WizardWrapper: FunctionComponent<WizardWrapper> = ({
  noLogin,
  onLogout,
  sectionRef,
  artists,
  user,
}) => {
  const result = useBackendLoginHook({ artists, user });

  if ((!result.allowOnboarding || !result.user) && !noLogin) {
    return (
      <div ref={sectionRef}>
        <Box display="flex" justifyContent="center" pt={15} pb={14}>
          <CircleAnimations />
        </Box>
      </div>
    );
  }

  return (
    <Wizard
      noLogin={noLogin}
      onLogout={onLogout}
      sectionRef={sectionRef}
      user={
        noLogin
          ? null
          : {
              //@ts-ignore
              spotifyId: result.user.spotifyId,
              artists: result.artists,
              genres: result.genres,
            }
      }
    />
  );
};

type Wizard = {
  noLogin: boolean;
  onLogout: () => void;
  sectionRef: MutableRefObject<any>;
  user: User | null;
};

const Wizard: FunctionComponent<Wizard> = ({
  noLogin,
  onLogout,
  sectionRef,
  user,
}) => {
  const { formatMessage } = useIntl();
  const [clubs, setClubs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formState, formDispatch] = useReducer(formReducer, formInitialState);
  const [stepState, stepDispatch] = useReducer(
    stepReducer,
    initialStepState(!!user)
  );

  const previousStep = () => {
    const current = stepState.current;
    if (current === 'CLUB_BAR' && !!user) {
      stepDispatch({ type: current, payload: 'COUNTRY_CITY' });
    } else if (current === 'LIST' && !!user) {
      stepDispatch({ type: current, payload: 'CLUB_BAR' });
    } else if (current === 'MAP') {
      stepDispatch({ type: current, payload: 'LIST' });
    } else if (current === 'MEET') {
      stepDispatch({ type: current, payload: 'MAP' });
    } else if (current === 'CHAT') {
      stepDispatch({ type: current, payload: 'MEET' });
    } else {
      onLogout();
    }
  };

  const nextStep = async () => {
    const current = stepState.current;
    if (current === 'COUNTRY_CITY') {
      stepDispatch({ type: current, payload: 'CLUB_BAR' });
    } else if (current === 'CLUB_BAR') {
      if (!!user) {
        await submit(user);
      }
      stepDispatch({ type: current, payload: 'LIST' });
    } else if (current === 'LIST') {
      stepDispatch({ type: current, payload: 'MAP' });
    } else if (current === 'MAP') {
      stepDispatch({ type: current, payload: 'MEET' });
    } else if (current === 'MEET') {
      stepDispatch({ type: current, payload: 'CHAT' });
    }
  };

  const submit = async (user: User) => {
    setSubmitting(true);

    const clubs = await backendClient(user.spotifyId).onboarding({
      bar: formState.bar,
      club: formState.club,
      city: formState.city,
      country: formState.country.key,
    });

    setClubs(clubs);

    setSubmitting(false);
  };

  const allowbars = formState.city.length > 1;
  const allowSubmit = formState.club.length > 1 && formState.bar.length > 1;
  const disabled =
    !noLogin &&
    ((!allowbars && stepState.index > 0) ||
      (!allowSubmit && stepState.index > 1));

  const nivgation = (
    <WizardNavigation
      disabled={disabled}
      step={stepState.index}
      user={user}
      actions={{ previousStep, nextStep, onLogout }}
    />
  );

  const secondaryStep = submitting ? (
    <Box
      display="flex"
      minHeight="240px"
      justifyContent="center"
      alignItems="center"
    >
      <CircularProgress />
    </Box>
  ) : (
    <>
      <WizardSecondaryStep
        formState={formState}
        formDispatch={formDispatch}
        step={stepState.index}
      />
      {stepState.index < 3 && nivgation}
    </>
  );

  return (
    <ContentSection
      sectionRef={sectionRef}
      alignment="left"
      supportContent={
        <Box maxWidth="400px" margin="0 auto">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            minHeight="240px"
          >
            {secondaryStep}
          </Box>
        </Box>
      }
    >
      <Typography variant="h3">
        {formatMessage(
          { id: 'features.title' },
          { logo: <Logo key="section.features.logo" /> }
        )}
      </Typography>

      <WizardSteps step={stepState.index} />
      {user && stepState.current === 'LIST' && (
        <Clubs city={formState.city} clubs={clubs} user={user} />
      )}
      {stepState.index >= 3 && nivgation}
    </ContentSection>
  );
};

export default WizardWrapper;

type Clubs = {
  city: string;
  clubs: any[];
  user: User;
};

const Clubs: FunctionComponent<Clubs> = ({ city, clubs, user }) => {
  const { formatMessage } = useIntl();

  if (clubs.length < 1) {
    return (
      <Box mb={2}>
        <Typography>
          {formatMessage({ id: 'features.withLoginWizard.noResults' })}
        </Typography>
      </Box>
    );
  }

  if (!user.artists?.mediumTerm || !user.genres?.mediumTerm) {
    return (
      <Box mb={2}>
        <Typography>
          {formatMessage({ id: 'features.withLoginWizard.noResults' })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography>
        {formatMessage(
          { id: 'features.yourTop' },
          {
            clubs: clubs
              .filter((club: any) => club.score > 49)
              .map((club) => club.name)
              .join(', '),
            city,
          }
        )}
      </Typography>
      <Box mt={2}>
        {clubs
          .filter((club: any) => club.score < 50)
          .slice(0, 3)
          .map((club) => {
            return <Club club={club} user={user} />;
          })}
        {clubs.filter((club: any) => club.score < 50).length < 3 && (
          <Box mb={2}>
            {formatMessage({ id: 'features.withLoginWizard.fewResults' })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

type Club = {
  club: any;
  user: User;
};

const Club: FunctionComponent<Club> = ({ club, user: { artists, genres } }) => {
  if (!artists?.mediumTerm || !genres?.mediumTerm) {
    return null;
  }

  const userArtists = new Set(artists.mediumTerm);
  const userGenres = new Set(genres.mediumTerm?.map(([genre]) => genre));
  const { matchingArtists, matchingGenres } = getMatchingGenresAndArtists(
    club,
    userArtists,
    userGenres
  );

  return (
    <Box mb={2}>
      <Box pb={1}>
        <Typography variant="h3">{club.name}</Typography>
      </Box>
      <Box>
        <Box display="flex">
          <Box mr={1}>
            <Typography variant="subtitle1">Bands: </Typography>
          </Box>
          <Box>
            {matchingArtists.map((artist) => {
              return (
                <Chip
                  key={`artist:${artist}`}
                  inverse={!userArtists.has(artist)}
                >
                  {artist}
                </Chip>
              );
            })}
          </Box>
        </Box>
        <Box display="flex">
          <Box mr={1}>
            <Typography variant="subtitle1">Genres: </Typography>{' '}
          </Box>
          <Box>
            {matchingGenres.map((genre) => {
              return (
                <Chip key={`genre:${genre}`} inverse={!userGenres.has(genre)}>
                  {genre}
                </Chip>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const getMatchingGenresAndArtists = (
  { users, externalGenres, externalArtistsExtended }: ClubModal,
  userArtists: Set<string>,
  userGenres: Set<string>
) => {
  const clubGenres = getMatching(users, externalGenres, 'genres');
  const clubArtists = getMatching(users, externalArtistsExtended, 'artists');

  const artists = sortMatchingFirst(clubArtists, userArtists, 5);
  const genres = sortMatchingFirst(clubGenres, userGenres, 5);

  return {
    matchingArtists: artists,
    matchingGenres: genres,
  };
};

const sortMatchingFirst = (
  values: string[],
  set: Set<string>,
  limit: number
) => {
  return values
    .sort((a, b) => {
      return set.has(a) && !set.has(b) ? -1 : 1;
    })
    .slice(0, limit);
};

const getMatching = (
  clubUsers: UserModel[],
  external: string[],
  type: 'artists' | 'genres'
) => {
  let items = new Map<string, number>(external.map((item) => [item, 1]));

  for (const user of clubUsers) {
    for (const item of user[type]) {
      const scoredItem = items.get(item);
      if (scoredItem) {
        items.set(item, scoredItem + 1);
      } else {
        items.set(item, 1);
      }
    }
  }
  return [
    ...new Set(
      [...items.entries()].sort((a, b) => a[1] - b[1]).map((item) => item[0])
    ),
  ];
};

type Chip = {
  inverse?: boolean;
  onClick?: () => void;
};

export const Chip: React.FC<Chip> = ({ children, onClick, inverse }) => {
  return (
    <span
      onClick={onClick}
      style={{
        background: inverse ? 'rgb(255, 255, 255)' : 'rgb(241, 72, 72)',
        color: inverse ? 'rgb(33, 33, 33)' : 'rgb(255, 255, 255)',
        border: inverse ? 'solid rgba(0,0,0,0.05) 1px' : 'none',
        display: 'inline-block',
        borderRadius: '10px',
        lineHeight: '16px',
        fontSize: 12,
        fontWeight: 600,
        padding: '2px 6px',
        margin: '0 4px 4px 0',
      }}
    >
      {children}
    </span>
  );
};
